import type {TFunction} from 'i18next'
import {Tag} from '@/components/ui/tag'
import {CeldaEstado} from '@/components/ui/celdas-tabla'
import {HelpIcon} from '@/components/help-icon'
import type {Column} from '@/components/data-table'
import {CLASIFICACION_TONE, INTERPRETACION_TONE, type Fila} from '@/hooks/sgsst/riesgos/use-ipevr'

// `origen_ref` is `bateria:{año}:{dominio}`; a malformed ref yields the chip without a year.
function bateriaYear(fila: Fila): string | null {
  if (fila.origen !== 'bateria') return null
  const parts = (fila.origen_ref ?? '').split(':')
  return parts.length >= 2 && /^\d+$/.test(parts[1]) ? parts[1] : null
}

function camposSugeridos(fila: Fila): 'proceso' | 'zona' | 'ambos' | null {
  const proceso = fila.proceso_origen === 'ia'
  const zona = fila.zona_origen === 'ia'
  if (proceso && zona) return 'ambos'
  if (proceso) return 'proceso'
  if (zona) return 'zona'
  return null
}

export function ContextoLinea({fila, t}: {fila: Fila; t: TFunction}) {
  if (!fila.proceso && !fila.zona) return null
  const sugerido = camposSugeridos(fila)
  return (
    <span className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
      <span className="truncate">{[fila.proceso, fila.zona].filter(Boolean).join(' · ')}</span>
      {sugerido !== null && (
        <Tag tone="neutral" className="shrink-0 whitespace-nowrap" data-testid={`fila-${fila.id}-contexto-ia`}>
          {t('ipevr.grid.contextoIaChip')}
          <span className="sr-only">{t(`ipevr.grid.contextoIaSr.${sugerido}`)}</span>
        </Tag>
      )}
    </span>
  )
}

export function FilaCard({fila, onClick, t}: {fila: Fila; onClick: () => void; t: TFunction}) {
  return (
    <li
      className="border-b border-border last:border-0"
      data-testid="ipevr-fila-card"
      data-highlight-id={`fila-${fila.id}`}>
      <button
        type="button"
        onClick={onClick}
        className="flex w-full min-h-11 flex-col gap-2 px-4 py-3 text-left outline-none hover:bg-secondary/50 focus-visible:bg-secondary/50 focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-inset">
        <div className="space-y-1">
          <p className="text-sm text-foreground break-words">{fila.descripcion}</p>
          <ContextoLinea fila={fila} t={t} />
          {bateriaYear(fila) !== null && (
            <Tag tone="neutral" className="whitespace-nowrap" data-testid={`fila-${fila.id}-bateria`}>
              {t('ipevr.grid.bateriaChip', {anio: bateriaYear(fila)})}
            </Tag>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <Tag tone={CLASIFICACION_TONE[fila.clasificacion]} className="whitespace-nowrap">
            {t(`ipevr.clasificacion.${fila.clasificacion}`)}
          </Tag>
          {fila.interpretacion && (
            <Tag tone={INTERPRETACION_TONE[fila.interpretacion]}>
              {t('ipevr.grid.interpretacion')} {fila.interpretacion}
            </Tag>
          )}
          {fila.confirmado_en === null ? (
            <Tag tone="amber" className="whitespace-nowrap">
              {t('ipevr.grid.unconfirmed')}
            </Tag>
          ) : (
            <Tag tone="green" className="whitespace-nowrap">
              {t('ipevr.grid.confirmed')}
            </Tag>
          )}
          {fila.novedades_pendientes && (
            <Tag tone="amber" className="whitespace-nowrap">
              {t('ipevr.novedades.chip')}
            </Tag>
          )}
        </div>
        <p className="font-mono text-xs text-muted-foreground">
          {t('ipevr.grid.numExpuestos')}: {fila.num_expuestos ?? '—'}
        </p>
      </button>
    </li>
  )
}

// `table-fixed` + explicit `width` (data-table.tsx) prevent column overlap; descripcion is the
// only column without `width` and takes what's left.
export function buildColumns(t: TFunction): Column<Fila>[] {
  return [
    {
      key: 'descripcion',
      header: t('ipevr.grid.colPeligro'),
      // La ayuda va en el `th` (U4/T3): por celda quedaría dentro de una fila `role="link"`.
      headerAside: (
        <>
          <HelpIcon label={t('ipevr.tipLabel.bateria')} testId="ipevr-bateria-help">
            {t('ipevr.grid.bateriaChipTip')}
          </HelpIcon>
          <HelpIcon label={t('ipevr.tipLabel.contexto')} testId="ipevr-contexto-help">
            {t('ipevr.grid.contextoOrigenTip')}
          </HelpIcon>
        </>
      ),
      sortable: true,
      className: 'align-middle',
      cell: (f) => (
        <CeldaEstado
          chip={
            bateriaYear(f) !== null ? (
              <Tag tone="neutral" className="whitespace-nowrap" data-testid={`fila-${f.id}-bateria`}>
                {t('ipevr.grid.bateriaChip', {anio: bateriaYear(f)})}
              </Tag>
            ) : undefined
          }
          nombre={<span className="line-clamp-2 font-normal text-foreground break-words">{f.descripcion}</span>}
          meta={<ContextoLinea fila={f} t={t} />}
        />
      ),
    },
    {
      key: 'clasificacion',
      header: t('ipevr.grid.clasificacion'),
      width: 'w-44',
      className: 'align-middle',
      sortable: true,
      cell: (f) => (
        <Tag
          tone={CLASIFICACION_TONE[f.clasificacion]}
          className="whitespace-nowrap"
          data-testid={`fila-${f.id}-clasificacion`}>
          {t(`ipevr.clasificacion.${f.clasificacion}`)}
        </Tag>
      ),
    },
    {
      // w-28: «INTERPRETACIÓN» in mono uppercase is ~100px; narrower, the header wraps.
      key: 'interpretacion',
      header: t('ipevr.grid.interpretacion'),
      width: 'w-28',
      className: 'align-middle',
      sortable: true,
      cell: (f) =>
        f.interpretacion ? (
          <Tag tone={INTERPRETACION_TONE[f.interpretacion]} data-testid={`fila-${f.id}-interpretacion`}>
            {f.interpretacion}
          </Tag>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      key: 'estado',
      header: t('ipevr.grid.estadoColumn'),
      width: 'w-24',
      className: 'align-middle',
      sortable: true,
      // Sin `CeldaEstado` a propósito: es chip + nombre + meta y esta celda no tiene nombre.
      // `w-fit` agrupa los chips al ancho de su texto.
      cell: (f) => (
        <div className="flex w-fit flex-wrap items-center gap-1">
          {f.confirmado_en === null ? (
            <Tag tone="amber" className="whitespace-nowrap" data-testid={`fila-${f.id}-unconfirmed`}>
              {t('ipevr.grid.unconfirmed')}
            </Tag>
          ) : (
            <Tag tone="green" className="whitespace-nowrap" data-testid={`fila-${f.id}-confirmed`}>
              {t('ipevr.grid.confirmed')}
            </Tag>
          )}
          {f.novedades_pendientes && (
            <Tag tone="amber" title={t('ipevr.novedades.chipPorque')} data-testid={`fila-${f.id}-novedades`}>
              {t('ipevr.novedades.chip')}
              <span className="sr-only"> {t('ipevr.novedades.chipPorque')}</span>
            </Tag>
          )}
        </div>
      ),
    },
    {
      key: 'num_expuestos',
      header: t('ipevr.grid.numExpuestos'),
      width: 'w-20',
      className: 'text-right align-middle',
      sortable: true,
      cell: (f) => <span className="font-mono text-xs text-foreground">{f.num_expuestos ?? '—'}</span>,
    },
  ]
}
